/**
 * AP-29-A/F — Die Modulseite des Mandanten.
 *
 * Die Liste kommt aus der Registry (`@symphenia/shared-types`) und wird vom
 * Backend mitgeliefert — vorher stand sie hier ein zweites Mal.
 *
 * Seit AP-29-F ist sie mehr als ein Katalog: Sie beantwortet die drei Fragen,
 * die ein Betrieb an seine Module hat — **läuft es noch** (Testphase,
 * Laufzeit, Kündigung), **reicht es für alle** (Platzstand) und **wie komme ich
 * an mehr** (anfragen). Die Anfrage schaltet nichts frei; sie erreicht den
 * Betreiber, der in seiner Konsole bucht (AP-29, Festlegung „Betreiber-Konsole
 * + Anfrage").
 */
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { Modal } from '@symphenia/ui-components';
import { tenantsApi, type ModuleCatalogEntry } from '@/features/tenants/api/tenants.api';
import { moduleRequestsApi, type ModuleRequestKind } from '../api/module-requests.api';
import { useTerminology } from '@/lib/useTerminology';
import { moduleLabel } from '@/lib/module-label';
import { pageTitle, formCard } from '@/lib/form-styles';
import { Package, CheckCircle, XCircle, Clock, AlertTriangle, MailCheck } from 'lucide-react';

/** Was im Dialog steht, während er offen ist. */
type Anfrage = { modul: ModuleCatalogEntry; kind: ModuleRequestKind };

const datum = (iso: string) => new Date(iso).toLocaleDateString('de-AT');

/**
 * AP-29-N — Wie ein Posten auf dieser Seite heißt.
 *
 * Drei Quellen, in dieser Reihenfolge:
 *
 * 1. die **Vertriebsbezeichnung** des Betreibers — eine ausdrückliche
 *    Festlegung, sie geht vor;
 * 2. der **Begriff des Mandanten** bei `PROJECT_MGMT` (AP-16: Projekt oder
 *    Auftrag) — solange der Betreiber nichts festgelegt hat, entscheidet der
 *    Betrieb, wie seine Aufträge heißen;
 * 3. die **i18n-Vorgabe**.
 *
 * Die Reihenfolge ist der Grund, warum das hier steht und nicht in
 * `moduleLabel`: Die Terminologie ist eine Eigenheit dieser einen Seite; der
 * gemeinsame Helfer kennt sie nicht und soll sie nicht kennen.
 */
function modulName(
  mod: Pick<ModuleCatalogEntry, 'key' | 'salesName'>,
  t: TFunction,
  terms: { management: string },
): string {
  if (mod.salesName) return mod.salesName;
  if (mod.key === 'PROJECT_MGMT') return terms.management;
  return moduleLabel(mod.key, t);
}

/** Volle Tage bis zu einem Zeitpunkt; negativ, wenn er vorbei ist. */
function tageBis(iso: string): number {
  const ms = new Date(iso).getTime() - Date.now();
  return Math.ceil(ms / (24 * 60 * 60 * 1000));
}

/**
 * Läuft die Buchung gerade — unabhängig davon, ob **dieser** Benutzer einen Platz hat?
 *
 * Ohne `booking` (ein älterer Backend-Stand im Deploy-Fenster) zählt
 * `bookedForTenant`. Sonst behauptete die Seite für ein längst gebuchtes Modul
 * „nicht gebucht" und böte an, es anzufragen.
 */
function laeuft(mod: ModuleCatalogEntry): boolean {
  if (!mod.booking) return mod.bookedForTenant;
  return mod.booking.status === 'ACTIVE' || mod.booking.status === 'TRIAL';
}

/**
 * Welches Anliegen trägt diese Zeile — oder keines?
 *
 * Eine Stelle für die Entscheidung, weil sie an zwei Orten gebraucht wird: für
 * den Knopf in der Zeile **und** dafür, ob die Aktionsspalte überhaupt erscheint.
 * Stünden die beiden getrennt, gäbe es eine Spaltenüberschrift ohne Inhalt —
 * oder umgekehrt einen Knopf ohne Spalte.
 */
function anliegen(mod: ModuleCatalogEntry): ModuleRequestKind | null {
  // Gebucht: Nachkaufen lässt sich nur, wo es überhaupt Plätze gibt.
  if (laeuft(mod)) return mod.booking?.seatMode === 'ASSIGNED' ? 'SEATS' : null;

  // Nicht gebucht: verkäuflich → anfragen, sonst → vormerken. Ein geplantes
  // Modul bekommt weiterhin keinen Verkaufsknopf (AP-29-A), aber einen
  // Rückkanal. Ohne ihn bleibt die Seite für einen Betrieb, der alles
  // Verkäufliche hat, ganz ohne Handlung — der Zustand, der am 11.9.2026
  // gemeldet wurde.
  return mod.bookable ? 'MODULE' : 'INTEREST';
}

export default function ModulesPage() {
  const { t } = useTranslation();
  // AP-16: Bei einem ORDER-Mandanten heißt das Modul in der Navigation
  // „Auftragsverwaltung". Stünde hier der feste Name, hieße dasselbe Modul an
  // zwei Stellen verschieden — und ausgerechnet der Katalog wäre die falsche.
  const terms = useTerminology();
  const [catalog, setCatalog] = useState<ModuleCatalogEntry[]>([]);

  /**
   * AP-29-N-H: Jeder Baustein direkt unter seiner Gruppe.
   *
   * Die Registry-Reihenfolge tut das schon, aber sie ist nicht garantiert — und
   * eine Liste, in der ein Teil zehn Zeilen unter seinem Modul steht, liest
   * niemand als Zusammenhang. Ein Modul, das der Betrieb gar nicht hat, bleibt
   * eine einzelne Zeile: Erst wer drin ist, sieht die Teile.
   */
  const gestaffelt = useMemo(() => {
    const gruppen = catalog.filter((m) => !m.parent);
    return gruppen.flatMap((g) => {
      const teile = catalog.filter((m) => m.parent === g.key);
      const zeigeTeile = g.parts !== null && (g.bookedForTenant || teile.some((t) => t.bookedForTenant));
      return zeigeTeile ? [g, ...teile] : [g];
    });
  }, [catalog]);
  // AP-29-F: Bei „alle Benutzer" gibt es keine Plätze zu zählen — wohl aber
  // Menschen. Die Zahl macht aus einer Betriebsart eine Auskunft.
  const [aktiveBenutzer, setAktiveBenutzer] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [anfrage, setAnfrage] = useState<Anfrage | null>(null);

  const laden = useMemo(
    () => () =>
      tenantsApi
        .getModules()
        // Fallback: Ein Backend ohne `catalog` (aelterer Stand im Deploy-Fenster)
        // soll die Seite nicht weiss werden lassen.
        .then((res) => {
          setCatalog(res.data.catalog ?? []);
          setAktiveBenutzer(res.data.activeUsers ?? null);
        })
        .catch(() => setError(t('common.error')))
        .finally(() => setLoading(false)),
    [t],
  );

  useEffect(() => {
    void laden();
  }, [laden]);

  if (loading) {
    return (
      <div className="p-6">
        <p className="text-muted-foreground">{t('common.loading')}</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <p className="text-destructive">{error}</p>
      </div>
    );
  }

  // Die Aktionsspalte trägt nur dort etwas, wo es etwas anzufragen gibt. Hat
  // keine Zeile eine Aktion, bleibt sonst eine Spaltenüberschrift ohne Inhalt.
  const hatAktionen = catalog.some((m) => anliegen(m) !== null);

  return (
    <div className="mx-auto max-w-5xl p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center gap-3">
          <Package size={24} className="text-primary" />
          <h1 className={pageTitle}>{t('modules.title')}</h1>
        </div>
        <p className="mt-1 text-sm text-muted-foreground">{t('modules.subtitle')}</p>
      </div>

      {/* Module-Tabelle */}
      <div className={formCard}>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border text-left text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                <th className="pb-3 pr-4">{t('modules.name')}</th>
                <th className="pb-3 pr-4">{t('modules.status')}</th>
                <th className="pb-3 pr-4">{t('modules.seats')}</th>
                <th className="pb-3 pr-4">{t('modules.description')}</th>
                {hatAktionen && <th className="pb-3">{t('modules.action')}</th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-border/50">
              {gestaffelt.map((mod) => (
                <tr key={mod.key} className="group align-top">
                  <td className="py-3 pr-4 font-medium text-foreground">
                    {/*
                      AP-29-N-H: Ein Baustein rückt unter seine Gruppe ein. Ohne
                      die Staffelung stünden hier bald vierzig gleichrangige
                      Zeilen, und niemand sähe mehr, was zusammengehört.
                    */}
                    <span className={mod.parent ? 'ml-5 font-normal' : undefined}>
                      {modulName(mod, t, terms)}
                    </span>
                    {/*
                      Die vierte Frage, die es vor der Zerlegung nicht geben
                      konnte: *was fehlt mir noch*. Sie steht an der Gruppe, weil
                      der Betrieb in Modulen denkt und in Teilen kauft.
                    */}
                    {mod.parts && (
                      <span className="ml-2 text-xs font-normal text-muted-foreground">
                        {t('modules.partsBooked', {
                          booked: mod.parts.booked,
                          total: mod.parts.total,
                        })}
                      </span>
                    )}
                  </td>
                  <td className="py-3 pr-4">
                    <StatusZelle mod={mod} />
                  </td>
                  <td className="py-3 pr-4">
                    <PlatzZelle mod={mod} aktiveBenutzer={aktiveBenutzer} />
                  </td>
                  <td className="py-3 pr-4 text-muted-foreground">
                    {/* Beschreibungen mit Projekt-/Auftragsbezug setzen den Begriff ein. */}
                    {mod.parts
                      ? null
                      : t(`modules.descriptions.${mod.key}`, { projectPlural: terms.projectPlural })}
                  </td>
                  {hatAktionen && (
                    <td className="py-3">
                      <AktionsZelle mod={mod} onAnfragen={(kind) => setAnfrage({ modul: mod, kind })} />
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {anfrage && (
        <AnfrageDialog
          anfrage={anfrage}
          onClose={() => setAnfrage(null)}
          onFertig={() => {
            setAnfrage(null);
            void laden();
          }}
        />
      )}
    </div>
  );
}

// ─── Status ───────────────────────────────────────────────────────────────────

/**
 * Was mit diesem Modul gerade ist.
 *
 * Der Status kommt aus der **Buchung**, nicht aus `active`: `active` heißt „ich
 * darf", und ein Administrator ohne eigenen Platz sähe sonst „nicht gebucht"
 * für ein Modul, das sein Betrieb längst hat. Genau diesen Unterschied nennt
 * der Hinweis am Ende beim Namen (AP-29-D).
 */
/**
 * AP-29-N-H: Eine **Gruppe** ist eine Überschrift, kein Angebot.
 *
 * Ohne diese Unterscheidung erbte die Zeile „Zeiterfassung" die Logik eines
 * Moduls und meldete „Geplant" samt „Interesse vormerken" — weil `bookable`
 * für eine Gruppe `false` ist. Beides ist sinnlos: Gebucht werden ihre Teile,
 * und die stehen direkt darunter.
 */
const istGruppe = (mod: ModuleCatalogEntry) => mod.parts !== null;

function StatusZelle({ mod }: { mod: ModuleCatalogEntry }) {
  const { t } = useTranslation();
  const b = mod.booking;

  if (istGruppe(mod)) return null;

  if (!laeuft(mod)) {
    // Ein abgelaufenes oder ausgesetztes Modul ist etwas anderes als eines, das
    // nie gebucht war — nur beim ersten führt der Weg zur Verlängerung.
    const text = !b
      ? mod.bookable
        ? t('modules.notBooked')
        : t('modules.planned')
      : b.status === 'SUSPENDED'
        ? t('modules.suspended')
        : b.endsAt
          ? t('modules.expiredOn', { date: datum(b.endsAt) })
          : t('modules.expired');
    const Icon = !b && !mod.bookable ? Clock : !b ? XCircle : AlertTriangle;
    return (
      <span
        className={`inline-flex items-center gap-1.5 text-xs font-medium ${
          b ? 'text-amber-600 dark:text-amber-400' : 'text-muted-foreground'
        }`}
      >
        <Icon size={14} />
        {text}
      </span>
    );
  }

  const restTage = b?.endsAt ? tageBis(b.endsAt) : null;

  return (
    <div className="space-y-1">
      <span className="inline-flex items-center gap-1.5 text-xs font-medium text-emerald-600 dark:text-emerald-400">
        <CheckCircle size={14} />
        {b?.status === 'TRIAL' ? t('modules.trial') : t('modules.active')}
      </span>
      {restTage !== null && b?.endsAt && (
        <p className="text-xs text-muted-foreground">
          {b.endKind === 'CANCELLED'
            ? t('modules.cancelledUntil', { date: datum(b.endsAt) })
            : restTage <= 0
              ? t('modules.endsToday')
              : restTage <= 14
                ? t('modules.endsInDays', { count: restTage })
                : t('modules.endsOn', { date: datum(b.endsAt) })}
        </p>
      )}
      {/* Gebucht, aber dieser Benutzer hat keinen Platz — die eine Auskunft, die
          ein Administrator sonst beim Vertrieb statt bei sich selbst sucht. */}
      {!mod.active && <p className="text-xs text-amber-600 dark:text-amber-400">{t('modules.noSeatForYou')}</p>}
    </div>
  );
}

// ─── Plätze ───────────────────────────────────────────────────────────────────

function PlatzZelle({ mod, aktiveBenutzer }: { mod: ModuleCatalogEntry; aktiveBenutzer: number | null }) {
  const { t } = useTranslation();
  const b = mod.booking;

  if (istGruppe(mod)) return null;
  if (!b || !laeuft(mod)) return <span className="text-xs text-muted-foreground">–</span>;

  // Bei „alle Benutzer" gibt es keine Plätze — aber es gibt eine Zahl, und sie
  // ist die Antwort auf „wie viele haben Zugang". Der Verweis bleibt trotzdem
  // stehen: Bis zum 11.9.2026 war die Verteilseite hier ausgeblendet und damit
  // für jeden Mandanten in dieser (häufigsten) Betriebsart unerreichbar.
  if (b.seatMode === 'ALL_USERS') {
    return (
      <div className="space-y-1">
        <p className="text-xs text-foreground">
          {aktiveBenutzer === null
            ? t('modules.allUsers')
            : t('modules.allUsersCount', { count: aktiveBenutzer })}
        </p>
        <PlatzVerweis mod={mod} />
      </div>
    );
  }

  const voll = b.seats !== null && b.seatsUsed >= b.seats;

  return (
    <div className="space-y-1">
      <p className={`text-xs font-medium ${voll ? 'text-amber-600 dark:text-amber-400' : 'text-foreground'}`}>
        {t('modules.seatsUsed', { used: b.seatsUsed, total: b.seats ?? '–' })}
      </p>
      {/* Ruhende Zuteilungen ausgeschiedener Mitarbeiter belegen keinen Platz —
          ohne den Hinweis wirkt die Zahl auf der Verteilseite falsch. */}
      {b.seatsDormant > 0 && (
        <p className="text-xs text-muted-foreground">{t('modules.seatsDormant', { count: b.seatsDormant })}</p>
      )}
      <PlatzVerweis mod={mod} />
    </div>
  );
}

function PlatzVerweis({ mod }: { mod: ModuleCatalogEntry }) {
  const { t } = useTranslation();
  return (
    <Link
      to={`/settings/modules/${mod.key}/plaetze`}
      className="inline-block text-xs font-medium text-primary hover:underline"
    >
      {t('modules.manageSeats')}
    </Link>
  );
}

// ─── Aktion ───────────────────────────────────────────────────────────────────

function AktionsZelle({
  mod,
  onAnfragen,
}: {
  mod: ModuleCatalogEntry;
  onAnfragen: (kind: ModuleRequestKind) => void;
}) {
  const { t } = useTranslation();
  const kind = anliegen(mod);

  if (istGruppe(mod) || !kind) return null;

  // Eine offene Anfrage wird nicht zum zweiten Mal angeboten: Der Betreiber
  // bekommt sie ohnehin nur einmal (Sperrfrist im Backend), und ein Knopf, der
  // nichts mehr auslöst, ist schlechter als ein Datum.
  if (mod.lastRequest && mod.lastRequest.kind === kind) {
    return (
      <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
        <MailCheck size={14} />
        {t(kind === 'INTEREST' ? 'modules.notedOn' : 'modules.requestedOn', {
          date: datum(mod.lastRequest.requestedAt),
        })}
      </span>
    );
  }

  // Eine Vormerkung ist keine Bestellung — sie trägt deshalb auch nicht das
  // Aussehen eines Verkaufsknopfs.
  const still = kind === 'INTEREST';

  return (
    <button
      type="button"
      onClick={() => onAnfragen(kind)}
      className={
        still
          ? 'inline-flex items-center gap-1 rounded-md border border-border px-3 py-1.5 text-xs font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground'
          : 'inline-flex items-center gap-1 rounded-md border border-primary/30 px-3 py-1.5 text-xs font-medium text-primary transition-colors hover:bg-primary/10'
      }
    >
      {kind === 'MODULE'
        ? t('modules.requestModule')
        : kind === 'SEATS'
          ? t('modules.requestSeats')
          : t('modules.noteInterest')}
    </button>
  );
}

// ─── Dialog ───────────────────────────────────────────────────────────────────

function AnfrageDialog({
  anfrage,
  onClose,
  onFertig,
}: {
  anfrage: Anfrage;
  onClose: () => void;
  onFertig: () => void;
}) {
  const { t } = useTranslation();
  const terms = useTerminology();
  const platzanfrage = anfrage.kind === 'SEATS';
  // Eine Vormerkung verspricht keinen Termin — der Dialog muss das sagen,
  // bevor jemand klickt, nicht danach.
  const vormerkung = anfrage.kind === 'INTEREST';

  const [zusatz, setZusatz] = useState('5');
  const [notiz, setNotiz] = useState('');
  const [sendet, setSendet] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);

  const name = modulName(anfrage.modul, t, terms);
  const anzahl = Number.parseInt(zusatz, 10);
  const gueltig = !platzanfrage || (Number.isFinite(anzahl) && anzahl >= 1);

  const senden = async () => {
    setSendet(true);
    setFehler(null);
    try {
      await moduleRequestsApi.create({
        moduleKey: anfrage.modul.key,
        kind: anfrage.kind,
        ...(platzanfrage ? { additionalSeats: anzahl } : {}),
        ...(notiz.trim() ? { note: notiz.trim() } : {}),
      });
      onFertig();
    } catch (e: unknown) {
      // Die Meldung des Servers ist konkreter als jede, die hier stehen könnte
      // („ist bereits gebucht", „konnte nicht zugestellt werden").
      const antwort = (e as { response?: { data?: { message?: string | string[] } } })?.response?.data
        ?.message;
      setFehler(Array.isArray(antwort) ? antwort.join(' · ') : (antwort ?? t('modules.requestFailed')));
      setSendet(false);
    }
  };

  return (
    <Modal
      isOpen
      onClose={onClose}
      size="md"
      title={
        platzanfrage
          ? t('modules.requestSeatsTitle', { module: name })
          : vormerkung
            ? t('modules.noteInterestTitle', { module: name })
            : t('modules.requestModuleTitle', { module: name })
      }
      footer={
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md border border-border px-4 py-2 text-sm text-foreground hover:bg-muted"
          >
            {t('common.cancel')}
          </button>
          <button
            type="button"
            onClick={() => void senden()}
            disabled={sendet || !gueltig}
            className="rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground disabled:opacity-50"
          >
            {sendet
              ? t('modules.requestSending')
              : vormerkung
                ? t('modules.noteInterestSend')
                : t('modules.requestSend')}
          </button>
        </div>
      }
    >
      <div className="space-y-4">
        {/* Kein Bestellformular: Die Anfrage schaltet nichts frei und kostet an
            dieser Stelle nichts — das gehört dazugesagt, bevor jemand klickt. */}
        <p className="text-sm text-muted-foreground">
          {vormerkung ? t('modules.noteInterestIntro') : t('modules.requestIntro')}
        </p>

        {platzanfrage && (
          <label className="block">
            <span className="mb-1 block text-sm font-medium text-foreground">
              {t('modules.requestSeatsLabel')}
            </span>
            <input
              type="number"
              min={1}
              value={zusatz}
              onChange={(e) => setZusatz(e.target.value)}
              className="w-32 rounded-md border border-border bg-background px-3 py-2 text-sm"
            />
            {anfrage.modul.booking?.seats !== null && anfrage.modul.booking !== null && (
              <span className="ml-3 text-xs text-muted-foreground">
                {t('modules.requestSeatsCurrent', { count: anfrage.modul.booking.seats ?? 0 })}
              </span>
            )}
          </label>
        )}

        <label className="block">
          <span className="mb-1 block text-sm font-medium text-foreground">{t('modules.requestNote')}</span>
          <textarea
            rows={4}
            value={notiz}
            onChange={(e) => setNotiz(e.target.value)}
            maxLength={2000}
            placeholder={
              vormerkung ? t('modules.noteInterestPlaceholder') : t('modules.requestNotePlaceholder')
            }
            className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm"
          />
        </label>

        {fehler && <p className="text-sm text-destructive">{fehler}</p>}
      </div>
    </Modal>
  );
}
